import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { getFileUrl, hrms } from '../../services/api';
import {
  Bell, Maximize2, Minimize2, User, KeyRound,
  LogOut, ChevronDown,
  Calendar, Home, MapPin, Receipt, CheckCircle,
  Users, RefreshCw, HelpCircle, AtSign,
} from 'lucide-react';
import FAQPanel from '../common/FAQPanel';

/* ── category config ─────────────────────────────────────────────────────── */
const CAT = {
  team_leave:        { icon: Calendar,     color: '#D97706', bg: '#FFFBEB', label: 'Team Leave'        },
  team_wfh:          { icon: Home,         color: '#2563EB', bg: '#EFF6FF', label: 'Team WFH'          },
  team_client_visit: { icon: MapPin,       color: '#7C3AED', bg: '#F5F3FF', label: 'Client Visit'      },
  my_leave:          { icon: Calendar,     color: '#16A34A', bg: '#F0FDF4', label: 'Leave'             },
  my_wfh:            { icon: Home,         color: '#0D9488', bg: '#F0FDFA', label: 'WFH'               },
  my_client_visit:   { icon: MapPin,       color: '#7C3AED', bg: '#F5F3FF', label: 'Client Visit'      },
  my_claim:          { icon: Receipt,      color: '#D97706', bg: '#FFFBEB', label: 'Claim'             },
  mention:           { icon: AtSign,       color: '#EA580C', bg: '#FFF7ED', label: 'Mention'           },
  birthday_wish:     { icon: Bell,         color: '#EC4899', bg: '#FDF2F8', label: 'Birthday'          },
};

const TEAM_CATS = new Set(['team_leave','team_wfh','team_client_visit']);

function relTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)return `${Math.floor(diff/3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short' });
}

function NotifItem({ n, isUnread, onClick }) {
  const cfg = CAT[n.category] || CAT.my_leave;
  const Icon = cfg.icon;
  return (
    <div
      onClick={onClick}
      style={{
        display:'flex', gap:'0.75rem', padding:'0.75rem 1rem',
        borderBottom:`1px solid #F1F5F9`, cursor:'pointer',
        background: isUnread ? '#FAFBFF' : '#fff',
        transition:'background 0.12s',
      }}
      onMouseEnter={e=>e.currentTarget.style.background='#F8FAFC'}
      onMouseLeave={e=>e.currentTarget.style.background=isUnread?'#FAFBFF':'#fff'}
    >
      <div style={{
        width:34, height:34, borderRadius:'50%', flexShrink:0,
        background:cfg.bg, display:'flex', alignItems:'center',
        justifyContent:'center', marginTop:2,
        border:`1px solid ${cfg.color}25`,
      }}>
        <Icon size={15} color={cfg.color} strokeWidth={2.25}/>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.25rem' }}>
          <span style={{ fontSize:'0.78rem', fontWeight: isUnread ? 700 : 600,
                         color:'#111827', lineHeight:1.35 }}>
            {isUnread && (
              <span style={{ display:'inline-block', width:6, height:6, borderRadius:'50%',
                             background:'#2563EB', marginRight:5, verticalAlign:'middle',
                             flexShrink:0 }}/>
            )}
            {n.title}
          </span>
          <span style={{ fontSize:'0.62rem', color:'#9CA3AF', flexShrink:0, marginTop:1 }}>
            {relTime(n.created_at)}
          </span>
        </div>
        <p style={{ margin:'0.2rem 0 0', fontSize:'0.73rem', color:'#6B7280',
                    lineHeight:1.4, overflow:'hidden', display:'-webkit-box',
                    WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {n.message}
        </p>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────────────── */
export default function Navbar({ user, onLogout }) {
  const navigate = useNavigate();
  const [showUserMenu,      setShowUserMenu]      = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFAQ,           setShowFAQ]           = useState(false);
  const [notifications,     setNotifications]     = useState([]);
  const [notiLoading,       setNotiLoading]       = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('readNotifIds_v2') || '[]')); }
    catch { return new Set(); }
  });
  const bellRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /* fullscreen listener */
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  /* close on outside click */
  useEffect(() => {
    const h = e => {
      if (bellRef.current && !bellRef.current.contains(e.target))
        setShowNotifications(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setNotiLoading(true);
    try {
      const res = await hrms.getNotifications();
      setNotifications(res.data || []);
    } catch { if (!silent) setNotifications([]); }
    finally { if (!silent) setNotiLoading(false); }
  }, []);

  /* auto-poll badge count every 60 s */
  useEffect(() => {
    fetchNotifications(true);
    const id = setInterval(() => fetchNotifications(true), 60_000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const openNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) fetchNotifications(false);
  };

  const markAllRead = () => {
    const next = new Set([...readIds, ...notifications.map(n => n.id)]);
    setReadIds(next);
    localStorage.setItem('readNotifIds_v2', JSON.stringify([...next]));
  };

  const handleClick = (n) => {
    setReadIds(prev => {
      const next = new Set([...prev, n.id]);
      localStorage.setItem('readNotifIds_v2', JSON.stringify([...next]));
      return next;
    });
    setShowNotifications(false);
    if (n.link) navigate(n.link);
  };



  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen();
    else document.exitFullscreen();
  };

  const roleLabels = {
    super_admin: 'Super Admin', hr_admin: 'HR Admin', it_admin: 'IT Admin',
    finance_team: 'Finance', finance_head: 'Finance Head', employee: 'Employee',
    reporting_manager: 'Manager', ceo: 'CEO', cfo: 'CFO', cto: 'CTO',
    project_manager: 'Project Manager', team_lead: 'Team Lead',
  };

  const iconBtn = {
    background:'none', border:'none', color:'#D1D5DB', cursor:'pointer',
    padding:'0.5rem', borderRadius:'50%', display:'flex', alignItems:'center',
    justifyContent:'center', width:'36px', height:'36px', transition:'background 0.15s',
  };

  /* group notifications — team items always shown (need action); personal only unread */
  const teamItems = notifications.filter(n => TEAM_CATS.has(n.category));
  const myItems   = notifications.filter(n => !TEAM_CATS.has(n.category) && !readIds.has(n.id));
  const visibleCount = teamItems.length + myItems.length;

  return (
    <nav style={{
      background: theme.dark, borderBottom:`3px solid ${theme.primary}`,
      height:'64px', display:'flex', alignItems:'center',
      justifyContent:'space-between', padding:'0 1.5rem',
      position:'fixed', top:0, left:0, right:0, zIndex:100,
      boxShadow:'0 2px 10px rgba(0,0,0,0.3)',
    }}>
      {/* Brand */}
      <div style={{ display:'flex', alignItems:'center' }}>
        <span style={{ fontSize:'1.15rem', fontWeight:700, letterSpacing:'-0.02em' }}>
          <span style={{ color:theme.primary }}>Bonton</span>
          <span style={{ color:'#FFFFFF' }}> Softwares Pvt Ltd</span>
        </span>
      </div>

      {/* Right actions */}
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>

        {/* Fullscreen */}
        <button onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          style={iconBtn}
          onMouseEnter={e=>e.currentTarget.style.background=theme.darkSecondary}
          onMouseLeave={e=>e.currentTarget.style.background='none'}>
          {isFullscreen ? <Minimize2 size={16} strokeWidth={1.75}/> : <Maximize2 size={16} strokeWidth={1.75}/>}
        </button>

        {/* Help / FAQ */}
        <button onClick={() => setShowFAQ(true)}
          title="Help & FAQ"
          style={iconBtn}
          onMouseEnter={e=>e.currentTarget.style.background=theme.darkSecondary}
          onMouseLeave={e=>e.currentTarget.style.background='none'}>
          <HelpCircle size={16} strokeWidth={1.75}/>
        </button>

        {/* Bell */}
        <div ref={bellRef} style={{ position:'relative' }}>
          <button onClick={openNotifications}
            title={visibleCount > 0 ? `${visibleCount} unread` : 'Notifications'}
            style={{ ...iconBtn, background:showNotifications?theme.darkSecondary:'none', position:'relative' }}
            onMouseEnter={e=>e.currentTarget.style.background=theme.darkSecondary}
            onMouseLeave={e=>e.currentTarget.style.background=showNotifications?theme.darkSecondary:'none'}>
            <Bell size={16} strokeWidth={1.75}/>
            {visibleCount > 0 && (
              <span style={{
                position:'absolute', top:'4px', right:'4px',
                background:'#EF4444', color:'#fff', borderRadius:'50%',
                width:'15px', height:'15px', fontSize:'0.58rem',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontWeight:700, pointerEvents:'none',
              }}>
                {visibleCount > 9 ? '9+' : visibleCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div style={{
              position:'absolute', right:0, top:'110%',
              background:'#fff', borderRadius:'0.75rem',
              boxShadow:'0 8px 32px rgba(0,0,0,0.16)',
              border:'1px solid #E2E8F0', width:'380px',
              zIndex:200, overflow:'hidden',
            }}>
              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                            padding:'0.75rem 1rem', borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  <Bell size={14} color='#374151' strokeWidth={2}/>
                  <span style={{ fontWeight:700, fontSize:'0.875rem', color:'#111827' }}>Notifications</span>
                  {visibleCount > 0 && (
                    <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#2563EB',
                                   background:'#EFF6FF', padding:'1px 7px',
                                   borderRadius:9999, border:'1px solid #BFDBFE' }}>
                      {visibleCount} new
                    </span>
                  )}
                </div>
                {myItems.length > 0 && (
                  <button onClick={markAllRead}
                    style={{ background:'none', border:'none', fontSize:'0.72rem',
                             color:'#6B7280', cursor:'pointer', fontWeight:600 }}>
                    Mark all read
                  </button>
                )}
              </div>

              <div style={{ maxHeight:'420px', overflowY:'auto' }}>
                {notiLoading ? (
                  <div style={{ padding:'2rem 1rem', textAlign:'center', color:'#9CA3AF', fontSize:'0.82rem' }}>
                    <RefreshCw size={20} strokeWidth={1.5} style={{ marginBottom:'0.5rem', animation:'spin 1s linear infinite' }}/>
                    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                    <p style={{ margin:0 }}>Loading notifications…</p>
                  </div>
                ) : visibleCount === 0 ? (
                  <div style={{ padding:'2.5rem 1rem', textAlign:'center' }}>
                    <Bell size={28} strokeWidth={1.25} color='#CBD5E1'/>
                    <p style={{ fontSize:'0.82rem', color:'#94A3B8', margin:'0.5rem 0 0', fontWeight:500 }}>
                      You're all caught up!
                    </p>
                    <p style={{ fontSize:'0.72rem', color:'#CBD5E1', margin:'0.25rem 0 0' }}>
                      No pending requests or updates
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Team action requests */}
                    {teamItems.length > 0 && (
                      <>
                        <div style={{ padding:'0.5rem 1rem 0.25rem', background:'#F8FAFC' }}>
                          <span style={{ fontSize:'0.63rem', fontWeight:700, color:'#94A3B8',
                                         textTransform:'uppercase', letterSpacing:'0.07em',
                                         display:'flex', alignItems:'center', gap:'0.35rem' }}>
                            <Users size={10} strokeWidth={2.5}/> Needs Your Action
                          </span>
                        </div>
                        {teamItems.map(n => (
                          <NotifItem key={n.id} n={n} isUnread={!readIds.has(n.id)}
                            onClick={() => handleClick(n)}/>
                        ))}
                      </>
                    )}

                    {/* Own request updates */}
                    {myItems.length > 0 && (
                      <>
                        <div style={{ padding:'0.5rem 1rem 0.25rem', background:'#F8FAFC',
                                      borderTop: teamItems.length ? '1px solid #F1F5F9' : 'none' }}>
                          <span style={{ fontSize:'0.63rem', fontWeight:700, color:'#94A3B8',
                                         textTransform:'uppercase', letterSpacing:'0.07em',
                                         display:'flex', alignItems:'center', gap:'0.35rem' }}>
                            <CheckCircle size={10} strokeWidth={2.5}/> Your Updates
                          </span>
                        </div>
                        {myItems.map(n => (
                          <NotifItem key={n.id} n={n} isUnread={!readIds.has(n.id)}
                            onClick={() => handleClick(n)}/>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div style={{ position:'relative' }}>
          <button onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              background:'none', border:'none', cursor:'pointer',
              display:'flex', alignItems:'center', gap:'0.625rem',
              padding:'0.4rem 0.75rem', borderRadius:'0.5rem',
              color:'#D1D5DB', transition:'background 0.15s',
            }}
            onMouseEnter={e=>e.currentTarget.style.background=theme.darkSecondary}
            onMouseLeave={e=>e.currentTarget.style.background='none'}>
            <div style={{
              width:'34px', height:'34px', borderRadius:'50%',
              background:theme.primary, display:'flex', alignItems:'center',
              justifyContent:'center', fontWeight:700, fontSize:'0.9rem',
              color:theme.white, flexShrink:0, overflow:'hidden',
            }}>
              {user?.profile_photo
                ? <img src={getFileUrl(user.profile_photo)} alt={user.full_name}
                    style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : (user?.full_name ? user.full_name.charAt(0).toUpperCase() : 'U')}
            </div>
            <div style={{ textAlign:'left' }}>
              <p style={{ fontSize:'0.85rem', fontWeight:600, color:'#F3F4F6', margin:0, lineHeight:1.2 }}>
                {user?.full_name || user?.username || 'User'}
              </p>
              <p style={{ fontSize:'0.7rem', color:'#9CA3AF', margin:0, lineHeight:1.4 }}>
                {user?.position_name || roleLabels[user?.role] || user?.role}
                {user?.emp_code ? ` · ${user.emp_code}` : ''}
              </p>
            </div>
            <ChevronDown size={14} strokeWidth={2} style={{ color:'#9CA3AF' }}/>
          </button>

          {showUserMenu && (
            <div style={{
              position:'absolute', right:0, top:'110%',
              background:theme.white, borderRadius:'0.5rem',
              boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
              border:`1px solid ${theme.border}`,
              minWidth:'180px', zIndex:200, overflow:'hidden',
            }}>
              <div style={{ padding:'0.75rem 1rem', borderBottom:`1px solid ${theme.border}` }}>
                <p style={{ fontSize:'0.8rem', color:theme.textMuted, margin:0 }}>Signed in as</p>
                <p style={{ fontSize:'0.9rem', fontWeight:600, color:theme.text, margin:0 }}>{user?.username}</p>
              </div>

              {user?.emp_id && (
                <button onClick={() => { setShowUserMenu(false); navigate('/employee/my-profile'); }}
                  style={menuItem(theme)}
                  onMouseEnter={e=>e.currentTarget.style.background=theme.pageBg}
                  onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  <User size={15} strokeWidth={1.75} style={{ color:theme.textMuted }}/> My Profile
                </button>
              )}

              <button onClick={() => { setShowUserMenu(false); navigate('/account/change-password'); }}
                style={menuItem(theme)}
                onMouseEnter={e=>e.currentTarget.style.background=theme.pageBg}
                onMouseLeave={e=>e.currentTarget.style.background='none'}>
                <KeyRound size={15} strokeWidth={1.75} style={{ color:theme.textMuted }}/> Change Password
              </button>

              <button onClick={() => { setShowUserMenu(false); onLogout(); }}
                style={{ ...menuItem(theme), color:theme.danger, borderBottom:'none' }}
                onMouseEnter={e=>e.currentTarget.style.background='#FEF2F2'}
                onMouseLeave={e=>e.currentTarget.style.background='none'}>
                <LogOut size={15} strokeWidth={1.75} style={{ color:theme.danger }}/> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      <FAQPanel
        isOpen={showFAQ}
        onClose={() => setShowFAQ(false)}
        userRole={user?.role}
      />
    </nav>
  );
}

function menuItem(theme) {
  return {
    width:'100%', padding:'0.7rem 1rem', background:'none', border:'none',
    cursor:'pointer', textAlign:'left', color:theme.text, fontSize:'0.875rem',
    fontWeight:500, display:'flex', alignItems:'center', gap:'0.6rem',
    borderBottom:`1px solid ${theme.border}`, transition:'background 0.1s',
  };
}
